import React, { useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { api } from "../../api/client";
import DriverPicker, { loadWriteRefusal } from "./DriverPicker";

/**
 * The driver on one load, on its bill of lading (fe#815 driver assignment).
 *
 * No screen could put a driver on a trucking load before this: the API took
 * `driverId` on POST and PATCH, nothing sent it, and the demo loads had theirs
 * from seeding. Editable until the BOL is signed. Once signed the driver is
 * final, like the truck and trailer — and here the API enforces it (409
 * LOAD_DRIVER_LOCKED, api#1130), so the read-only state is not a rule only
 * this screen holds.
 *
 * PATCH sends `driverId` only when it changed: the driver's id, or `null` to
 * clear. The API's refusals are shown as sent — 409 DRIVER_LICENCE_EXPIRED
 * ("Mike Kowalski's licence expired 2026-08-31, before the work date …"),
 * 409 LOAD_DRIVER_LOCKED, 404 for another tenant's driver.
 *
 * The BOL read is `l.*` plus `driver_name` (joined on `loads.driver_id`).
 */
interface LoadLike {
  id: string | number;
  status?: string | null;
  signed_at?: string | null;
  driver_id?: string | null;
  driver_name?: string | null;
}

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : "");

export default function LoadDriverPanel({ load, onSaved }: { load: LoadLike; onSaved: () => void }) {
  const signed = !!load.signed_at;
  const cancelled = load.status === "cancelled";
  const driverId = str(load.driver_id);
  const driverName = str(load.driver_name);
  const [editing, setEditing] = useState(false);
  const [driver, setDriver] = useState(driverId);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  // A driver the read cannot name is a dash, never "No driver".
  const driverText = driverId ? driverName || "—" : "No driver";

  const start = () => {
    setDriver(driverId);
    setRefusal(null);
    setEditing(true);
  };

  const save = async () => {
    if (driver === driverId) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setRefusal(null);
    try {
      await api.trucking.loads.update(String(load.id), { driverId: driver || null });
      setEditing(false);
      onSaved();
    } catch (err) {
      setRefusal(loadWriteRefusal(err, "The driver was not saved."));
    } finally {
      setSaving(false);
    }
  };

  const select =
    "h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";

  return (
    <section
      aria-labelledby="load-driver-title"
      data-testid="load-driver-panel"
      className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Crew</p>
          <h2 id="load-driver-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Driver</h2>
        </div>
        {!signed && !cancelled && !editing && (
          <button
            type="button"
            onClick={start}
            className="inline-flex items-center gap-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50"
          >
            <Pencil className="size-4" aria-hidden="true" />
            {driverId ? "Change driver" : "Assign driver"}
          </button>
        )}
      </header>

      {editing ? (
        <div className="space-y-4 px-4 py-4">
          <div className="sm:max-w-sm">
            <label htmlFor="bol-driver" className="mb-1 block text-xs font-medium text-fieldiq-muted">Driver</label>
            <DriverPicker
              id="bol-driver"
              value={driver}
              onChange={setDriver}
              currentId={driverId || undefined}
              currentName={driverName || null}
              className={select}
            />
          </div>
          {refusal && <p role="alert" data-testid="load-driver-refusal" className="text-sm font-medium text-fieldiq-text">{refusal}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={saving} className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">
              {saving && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Save driver
            </button>
            <button type="button" onClick={() => setEditing(false)} className="rounded-fi-control border border-fieldiq-border px-4 py-2.5 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="px-4 py-4">
          <div data-testid="load-driver-name">
            <dt className="text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">Driver</dt>
            <dd className="mt-0.5 text-sm text-fieldiq-text">{driverText}</dd>
          </div>
        </dl>
      )}

      {signed && (
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">Signed: the driver is final.</p>
      )}
    </section>
  );
}
